/**
 * ============================================================================
 * COMPOSABLE: Глобальный индикатор сохранения
 * ============================================================================
 * Управляет состоянием индикатора сохранения для всего приложения
 * ============================================================================
 */

import { ref, computed } from 'vue'

// Глобальное состояние (singleton)
const status = ref('saved') // saved, saving, unsaved, offline, error
const lastSaved = ref(null)
const isVisible = ref(false)
const hideTimeout = ref(null)

// Слушаем события соединения
if (typeof window !== 'undefined') {
	window.addEventListener('connection:offline', () => {
		if (status.value === 'unsaved' || status.value === 'saving') {
			status.value = 'offline'
			isVisible.value = true
		}
	})

	window.addEventListener('connection:online', () => {
		if (status.value === 'offline') {
			status.value = 'unsaved'
		}
	})
}

export function useSaveIndicator() {
	/**
	 * Установить статус "Сохранение..."
	 */
	function setSaving() {
		status.value = 'saving'
		isVisible.value = true
		clearHideTimeout()
	}

	/**
	 * Установить статус "Сохранено"
	 */
	function setSaved() {
		status.value = 'saved'
		lastSaved.value = Date.now()
		isVisible.value = true
		
		// Автоматически скрыть через 3 секунды
		scheduleHide(3000)
	}

	/**
	 * Установить статус "Есть несохранённые данные"
	 */
	function setUnsaved() {
		status.value = 'unsaved'
		isVisible.value = true
		clearHideTimeout()
	}

	/**
	 * Установить статус "Нет соединения"
	 */
	function setOffline() {
		status.value = 'offline'
		isVisible.value = true
		clearHideTimeout()
	}

	/**
	 * Установить статус "Ошибка"
	 */
	function setError() {
		status.value = 'error'
		isVisible.value = true
		
		// Автоматически скрыть через 5 секунд
		scheduleHide(5000)
	}

	/**
	 * Скрыть индикатор
	 */
	function hide() {
		isVisible.value = false
		clearHideTimeout()
	}

	/**
	 * Запланировать скрытие индикатора
	 */
	function scheduleHide(delay) {
		clearHideTimeout()
		hideTimeout.value = setTimeout(() => {
			hide()
		}, delay)
	}

	/**
	 * Отменить запланированное скрытие
	 */
	function clearHideTimeout() {
		if (hideTimeout.value) {
			clearTimeout(hideTimeout.value)
			hideTimeout.value = null
		}
	}

	return {
		status: computed(() => status.value),
		lastSaved: computed(() => lastSaved.value),
		isVisible: computed(() => isVisible.value),
		setSaving,
		setSaved,
		setUnsaved,
		setOffline,
		setError,
		hide,
	}
}
